import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, Minimize2, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('/roar-avatar.jpeg');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Generate or retrieve session ID
    let chatSessionId = sessionStorage.getItem('chat_session_id');
    if (!chatSessionId) {
      chatSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('chat_session_id', chatSessionId);
    }
    setSessionId(chatSessionId);

    // Fetch chatbot avatar
    fetchAvatar();

    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: 'Hei! Jeg er Sand-tec sin kundeserviceassistent. Hvordan kan jeg hjelpe deg i dag? 🚀',
      timestamp: new Date()
    }]);
  }, []);

  const fetchAvatar = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/avatar`);
      if (response.data.avatar_url) {
        setAvatarUrl(response.data.avatar_url);
      }
    } catch (error) {
      console.error('Error fetching avatar:', error);
      // Use default avatar
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      logger.userAction('chatbot_opened');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    logger.userAction('chatbot_message_sent', { message_length: input.length });

    try {
      const response = await axios.post(`${API}/chatbot`, {
        session_id: sessionId,
        message: input
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date(response.data.timestamp)
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chatbot error:', error);
      logger.error('error', 'Chatbot message failed', { error: error.message });
      
      const errorMessage = {
        role: 'assistant',
        content: 'Beklager, jeg har tekniske problemer. Vennligst prøv igjen eller kontakt oss direkte.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={handleToggle}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group"
          aria-label="Open chatbot"
        >
          <MessageCircle className="w-8 h-8 text-white group-hover:scale-110 transition-transform duration-300" />
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed z-50 transition-all duration-300 ${
            isMinimized
              ? 'bottom-6 right-6 w-80 h-16'
              : 'bottom-6 right-6 w-96 h-[600px]'
          }`}
        >
          <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-red-600/50 rounded-2xl shadow-2xl flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 flex items-center justify-between relative overflow-hidden">
              {/* Racing stripes animation */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute h-full w-1 bg-yellow-400 animate-pulse" style={{ left: '20%' }}></div>
                <div className="absolute h-full w-1 bg-yellow-400 animate-pulse" style={{ left: '40%', animationDelay: '0.2s' }}></div>
                <div className="absolute h-full w-1 bg-yellow-400 animate-pulse" style={{ left: '60%', animationDelay: '0.4s' }}></div>
                <div className="absolute h-full w-1 bg-yellow-400 animate-pulse" style={{ left: '80%', animationDelay: '0.6s' }}></div>
              </div>

              <div className="flex items-center space-x-3 relative z-10">
                <img
                  src={avatarUrl}
                  alt="Assistant"
                  className="w-10 h-10 rounded-lg object-cover border-2 border-white/30 shadow-lg"
                />
                <div>
                  <h3 className="font-bold text-white text-lg">Sand-tec Assistent</h3>
                  <p className="text-xs text-white/80">Alltid klar til å hjelpe</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 relative z-10">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200"
                >
                  <Minimize2 className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={handleToggle}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-900 to-black">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      } items-end space-x-2`}
                    >
                      {/* Assistant Avatar */}
                      {message.role === 'assistant' && (
                        <img
                          src={avatarUrl}
                          alt="Assistant"
                          className="w-8 h-8 rounded-full object-cover border-2 border-red-600 shadow-lg flex-shrink-0"
                        />
                      )}
                      
                      <div
                        className={`max-w-[75%] rounded-2xl p-4 ${
                          message.role === 'user'
                            ? 'bg-gradient-to-br from-red-600 to-red-700 text-white'
                            : 'bg-gray-800 text-gray-100 border border-gray-700'
                        } shadow-lg transform transition-all duration-200 hover:scale-105`}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {message.content}
                        </p>
                        <p className="text-xs mt-2 opacity-70">
                          {message.timestamp.toLocaleTimeString('no-NO', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}

                  {isLoading && (
                    <div className="flex justify-start items-end space-x-2">
                      <img
                        src={avatarUrl}
                        alt="Assistant"
                        className="w-8 h-8 rounded-full object-cover border-2 border-red-600 shadow-lg flex-shrink-0"
                      />
                      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-4 max-w-[75%]">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-red-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-red-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 bg-red-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 bg-gray-900 border-t border-gray-800">
                  <div className="flex space-x-2">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Skriv din melding..."
                      disabled={isLoading}
                      className="flex-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-red-600 rounded-xl"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!input.trim() || isLoading}
                      className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl px-6"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;